/**
 * Analytics Tracker - GA4 Ecommerce dataLayer Integration
 *
 * Listens to theme custom events and pushes GA4-compliant ecommerce data to dataLayer.
 * Does NOT modify existing cart-manager.js - only listens to its events.
 */
(function() {
  'use strict';

  const AnalyticsTracker = {
    DEBUG: false, // Set to true for console logging

    // =========================================================================
    // Utility Methods
    // =========================================================================

    /**
     * Get currency from Shopify or cart
     */
    getCurrency: function() {
      return window.Shopify?.currency?.active ||
             window.__analyticsContext?.currency ||
             'CAD';
    },

    /**
     * Get market (CA/US) based on currency
     */
    getMarket: function() {
      return this.getCurrency() === 'USD' ? 'US' : 'CA';
    },

    /**
     * Get user ID if customer is logged in
     */
    getUserId: function() {
      return window.__analyticsContext?.customerId || null;
    },

    /**
     * Get user data for enhanced conversions (Google Ads, Meta CAPI)
     */
    getUserData: function() {
      var ctx = window.__analyticsContext || {};
      if (!ctx.customerId) return null;

      return {
        email: ctx.customerEmail || null,
        email_hash: ctx.customerEmailHash || null,
        first_name: ctx.customerFirstName || null,
        last_name: ctx.customerLastName || null
      };
    },

    /**
     * Generate unique event ID for deduplication (browser + server-side tracking)
     */
    generateEventId: function() {
      return 'evt_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    },

    /**
     * Initialize dataLayer if not exists
     */
    initDataLayer: function() {
      window.dataLayer = window.dataLayer || [];
    },

    /**
     * Push event to dataLayer with global properties
     */
    push: function(eventData) {
      this.initDataLayer();

      // Clear previous ecommerce data (GA4 best practice)
      window.dataLayer.push({ ecommerce: null });

      // Generate unique event ID for deduplication
      var eventId = this.generateEventId();

      // Add global properties
      var enrichedData = {
        event_id: eventId,
        currency: this.getCurrency(),
        market: this.getMarket()
      };

      // Merge event data
      for (var key in eventData) {
        if (eventData.hasOwnProperty(key)) {
          enrichedData[key] = eventData[key];
        }
      }

      // Add user_id if available
      var userId = this.getUserId();
      if (userId) {
        enrichedData.user_id = userId;
      }

      // Add user_data for enhanced conversions (Google Ads, Meta)
      var userData = this.getUserData();
      if (userData) {
        enrichedData.user_data = userData;
      }

      window.dataLayer.push(enrichedData);

      // NOTE: this tracker deliberately does NOT push to the Meta Pixel (OPSAPPS-56).
      // Meta events are owned end-to-end by ONE integration (Shopify's Facebook & Instagram
      // channel, and later the Littledata connector). Firing fbq from here as well produced a
      // third, undeduplicable leg: the eventID generated below is random per-call, so no server
      // event could ever match it, and ViewContent/AddToCart/InitiateCheckout were double-counted.
      // The giveaway was ViewCategory — a non-standard event only this file emitted — sitting at
      // 1.4K/28d in Events Manager. Do not re-add an fbq call here.

      if (this.DEBUG) {
        console.log('[AnalyticsTracker]', eventData.event, enrichedData);
      }
    },

    /**
     * Format single item for GA4 ecommerce
     */
    formatItem: function(item, index, listName) {
      return {
        item_id: String(item.variant_id || item.id || item.product_id),
        item_name: item.product_title || item.title || item.name || '',
        item_brand: 'Tom & Sawyer',
        item_category: item.product_type || item.type || '',
        item_variant: item.variant_title || item.variant || '',
        price: (item.final_price || item.price || 0) / 100,
        quantity: item.quantity || 1,
        index: index || 0,
        ...(listName && { item_list_name: listName })
      };
    },

    /**
     * Format cart items array
     */
    formatCartItems: function(cart, listName) {
      if (!cart?.items) return [];
      return cart.items.map((item, index) => this.formatItem(item, index, listName));
    },

    /**
     * Calculate cart value in dollars
     */
    calculateCartValue: function(cart) {
      if (!cart?.total_price) return 0;
      return cart.total_price / 100;
    },

    /**
     * Detect cart type (meal_plan, build_your_own, or mixed)
     */
    getCartType: function(items) {
      if (!items || !items.length) return 'empty';

      const hasMealPlan = items.some(function(item) {
        return item.properties && item.properties._mealPlanId;
      });
      const hasBYO = items.some(function(item) {
        return item.properties && item.properties._bundleUid && !item.properties._mealPlanId;
      });

      if (hasMealPlan && hasBYO) return 'mixed';
      if (hasMealPlan) return 'meal_plan';
      return 'build_your_own';
    },

    /**
     * Extract meal plan details from cart items
     */
    getMealPlanDetails: function(items) {
      if (!items) return [];

      var mealPlans = {};
      var self = this;

      items.forEach(function(item) {
        if (item.properties && item.properties._mealPlanId) {
          var planId = item.properties._mealPlanId;
          if (!mealPlans[planId]) {
            mealPlans[planId] = {
              meal_plan_id: planId,
              pet_name: item.properties._petName || 'Unknown',
              pet_profile_id: item.properties._petProfileId || null,
              meal_count: 0,
              value: 0
            };
          }
          mealPlans[planId].meal_count++;
          mealPlans[planId].value += (item.final_line_price || 0) / 100;
        }
      });

      return Object.values(mealPlans);
    },

    // =========================================================================
    // Ecommerce Event Handlers
    // =========================================================================

    /**
     * view_item - Product page view
     */
    trackViewItem: function(product, variant) {
      if (!product) return;

      var price = (variant?.price || product.price || 0) / 100;

      this.push({
        event: 'view_item',
        ecommerce: {
          currency: this.getCurrency(),
          value: price,
          items: [{
            item_id: String(variant?.id || product.variants?.[0]?.id || product.id),
            item_name: product.title,
            item_brand: 'Tom & Sawyer',
            item_category: product.type || '',
            item_variant: variant?.title || '',
            price: price,
            quantity: 1
          }]
        }
      });
    },

    /**
     * view_item_list - Collection page view
     */
    trackViewItemList: function(listName, products) {
      if (!products?.length) return;

      var self = this;
      var items = products.slice(0, 20).map(function(product, index) {
        return {
          item_id: String(product.variant_id || product.variants?.[0]?.id || product.id),
          item_name: product.title,
          item_brand: 'Tom & Sawyer',
          item_category: product.type || '',
          item_list_name: listName,
          index: index,
          price: (product.price || 0) / 100
        };
      });

      this.push({
        event: 'view_item_list',
        ecommerce: {
          item_list_name: listName,
          items: items
        }
      });
    },

    /**
     * select_item - Product clicked from list
     */
    trackSelectItem: function(product, listName, index) {
      this.push({
        event: 'select_item',
        ecommerce: {
          item_list_name: listName,
          items: [this.formatItem(product, index, listName)]
        }
      });
    },

    /**
     * add_to_cart - Product added to cart
     */
    trackAddToCart: function(item, source) {
      if (!item) return;

      var value = (item.final_line_price || item.price || 0) / 100;

      this.push({
        event: 'add_to_cart',
        ecommerce: {
          currency: this.getCurrency(),
          value: value,
          items: [this.formatItem(item, 0)]
        },
        source: source || 'unknown'
      });
    },

    /**
     * remove_from_cart - Product removed from cart
     */
    trackRemoveFromCart: function(item) {
      if (!item) return;

      var value = (item.final_line_price || item.price || 0) / 100;

      this.push({
        event: 'remove_from_cart',
        ecommerce: {
          currency: this.getCurrency(),
          value: value,
          items: [this.formatItem(item, 0)]
        }
      });
    },

    /**
     * view_cart - Cart viewed
     */
    trackViewCart: function(cart) {
      if (!cart?.items?.length) return;

      this.push({
        event: 'view_cart',
        ecommerce: {
          currency: this.getCurrency(),
          value: this.calculateCartValue(cart),
          items: this.formatCartItems(cart)
        },
        cart_type: this.getCartType(cart.items),
        item_count: cart.item_count || cart.items.length
      });
    },

    /**
     * begin_checkout - Checkout initiated
     */
    trackBeginCheckout: function(cart) {
      if (!cart?.items?.length) return;

      var cartType = this.getCartType(cart.items);
      var eventData = {
        event: 'begin_checkout',
        ecommerce: {
          currency: this.getCurrency(),
          value: this.calculateCartValue(cart),
          items: this.formatCartItems(cart)
        },
        cart_type: cartType,
        item_count: cart.item_count || cart.items.length
      };

      // Add meal plan details if applicable
      if (cartType === 'meal_plan' || cartType === 'mixed') {
        var mealPlanDetails = this.getMealPlanDetails(cart.items);
        eventData.meal_plan_count = mealPlanDetails.length;
        eventData.meal_plan_details = mealPlanDetails;
      }

      this.push(eventData);
    },

    // =========================================================================
    // Subscription Events
    // =========================================================================

    /**
     * subscription_selected - User applied subscription
     */
    trackSubscriptionSelected: function(frequency, cart) {
      this.push({
        event: 'subscription_selected',
        subscription_frequency: frequency,
        cart_value: cart ? this.calculateCartValue(cart) : 0,
        cart_item_count: cart?.item_count || 0
      });
    },

    /**
     * subscription_frequency_changed - User changed frequency
     */
    trackSubscriptionFrequencyChanged: function(oldFrequency, newFrequency) {
      this.push({
        event: 'subscription_frequency_changed',
        previous_frequency: oldFrequency,
        new_frequency: newFrequency
      });
    },

    /**
     * subscription_removed - User switched to one-time
     */
    trackSubscriptionRemoved: function(cart) {
      this.push({
        event: 'subscription_removed',
        cart_value: cart ? this.calculateCartValue(cart) : 0,
        cart_item_count: cart?.item_count || 0
      });
    },

    // =========================================================================
    // Calculator Events
    // =========================================================================

    /**
     * calculator_opened - Calculator drawer opened
     */
    trackCalculatorOpened: function(source) {
      this.push({
        event: 'calculator_opened',
        source: source || 'unknown'
      });
    },

    /**
     * calculator_guide_requested - Feeding guide email submitted
     */
    trackCalculatorGuideRequested: function(petName, petType) {
      this.push({
        event: 'calculator_guide_requested',
        pet_name: petName,
        pet_type: petType,
        email_provided: true
      });
    },

    // =========================================================================
    // Filter Events
    // =========================================================================

    /**
     * filter_applied - Collection filter applied
     */
    trackFilterApplied: function(filterType, collectionHandle) {
      this.push({
        event: 'filter_applied',
        filter_type: filterType,
        collection_handle: collectionHandle
      });
    },

    // =========================================================================
    // Event Listeners
    // =========================================================================

    bindEvents: function() {
      var self = this;

      // -----------------------------------------------------------------------
      // Cart Events (from cart-manager.js)
      // -----------------------------------------------------------------------

      // Add to cart
      document.addEventListener('ajaxProduct:added', function(e) {
        var detail = e.detail || {};
        // Handle different payload structures
        var item = detail.product?.items?.[0] || detail.product || detail.item;
        var source = detail.source || 'product_page';
        if (item) {
          self.trackAddToCart(item, source);
        }
      });

      // Cart updated - could be used for various cart changes
      document.addEventListener('cart:updated', function(e) {
        // This fires frequently; we use it for subscription tracking
        // View cart is tracked separately on drawer open
      });

      // Cart drawer opened - track view_cart
      document.addEventListener('drawerOpen', function(e) {
        var drawer = e.target && e.target.classList ? e.target : document.querySelector('.drawer--is-open');
        if (drawer && drawer.classList && (drawer.classList.contains('drawer--cart') || drawer.id === 'CartDrawer')) {
          fetch('/cart.js')
            .then(function(res) { return res.json(); })
            .then(function(cart) { self.trackViewCart(cart); })
            .catch(function(err) {
              if (self.DEBUG) console.warn('[AnalyticsTracker] Failed to fetch cart:', err);
            });
        }
      });

      // -----------------------------------------------------------------------
      // Calculator Events
      // -----------------------------------------------------------------------

      window.addEventListener('calculator:drawer-opened', function() {
        // Try to determine source from trigger button
        var source = 'header_icon'; // Default
        self.trackCalculatorOpened(source);
      });

      // Calculator completion (from widget)
      window.addEventListener('ts:calculator-completed', function(e) {
        var detail = e.detail || {};
        self.trackCalculatorGuideRequested(
          detail.petName || 'Unknown',
          detail.petType || 'dog'
        );
      });

      // -----------------------------------------------------------------------
      // Product Grid Click (select_item)
      // -----------------------------------------------------------------------

      document.addEventListener('click', function(e) {
        var productCard = e.target.closest('[data-product-grid]');
        if (productCard) {
          var link = e.target.closest('a[href*="/products/"]');
          if (link) {
            var productId = productCard.dataset.id;
            var collectionTitle = window.__analyticsContext?.collection?.title || 'Product Grid';
            var allCards = document.querySelectorAll('[data-product-grid]');
            var index = Array.from(allCards).indexOf(productCard);

            var handle = link.href.split('/products/')[1]?.split('?')[0];
            var productData = window.mealProductData?.[handle] || {
              id: productId,
              title: productCard.querySelector('.grid-product__title')?.textContent?.trim() || '',
              type: productCard.dataset.productType || ''
            };

            self.trackSelectItem(productData, collectionTitle, index);
          }
        }
      });

      // -----------------------------------------------------------------------
      // Remove from Cart (intercept fetch calls)
      // -----------------------------------------------------------------------

      var originalFetch = window.fetch;
      window.fetch = function(url, options) {
        // Check if this is a cart change call with quantity 0 (removal)
        if (typeof url === 'string' && url.includes('/cart/change.js')) {
          var body = options?.body;
          if (body) {
            try {
              var data = typeof body === 'string' ? JSON.parse(body) : body;
              if (data.quantity === 0 || data.quantity === '0') {
                // Get current cart to find item being removed
                fetch('/cart.js')
                  .then(function(res) { return res.json(); })
                  .then(function(cart) {
                    var itemToRemove = cart.items.find(function(item) {
                      return item.key === data.id || String(item.variant_id) === String(data.id);
                    });
                    if (itemToRemove) {
                      self.trackRemoveFromCart(itemToRemove);
                    }
                  })
                  .catch(function() {});
              }
            } catch (err) {
              // Parsing failed, continue with original call
            }
          }
        }
        return originalFetch.apply(this, arguments);
      };
    },

    // =========================================================================
    // Page-Level Tracking
    // =========================================================================

    trackPageView: function() {
      var context = window.__analyticsContext || {};

      switch (context.pageType) {
        case 'product':
          if (context.product) {
            this.trackViewItem(context.product, context.variant);
          }
          break;

        case 'collection':
          if (context.collection && context.products) {
            this.trackViewItemList(context.collection.title, context.products);
          }
          break;

        case 'cart':
          if (context.cart) {
            this.trackViewCart(context.cart);
          }
          break;

        default:
          // Other page types - GTM handles basic page_view
          break;
      }
    },

    // =========================================================================
    // Initialization
    // =========================================================================

    init: function() {
      var self = this;

      this.initDataLayer();
      this.bindEvents();

      // Track initial page view after DOM ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function() {
          self.trackPageView();
        });
      } else {
        this.trackPageView();
      }

      if (this.DEBUG) {
        console.log('[AnalyticsTracker] Initialized');
      }
    }
  };

  // Expose globally for external calls (begin_checkout from cart page, etc.)
  window.AnalyticsTracker = AnalyticsTracker;

  // Auto-initialize
  AnalyticsTracker.init();

})();
